import React from 'react';
import type { ValidIconName, VoidCallback } from '../index';
import { ButtonIcon } from '../basic/internal';

export type WorklineNotificationType =
  | 'none'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

/**
 * Props for a {@link WorklineIcon} component.
 * @param notificationAriaLabel Set, if the notificationText is empty or very short and does not describe in detail what the notification is about.
 */
export type WorklineIconProps = {
  icon: ValidIconName;
  text: string;
  state: 'clickable' | 'disabled' | 'selected';
  highlightColor: 'primary' | 'secondary' | 'tertiary';
  onClick?: VoidCallback;
  notification: WorklineNotificationType;
  notificationText?: string;
  notificationAriaLabel?: string;
};

export function WorklineIcon({
  icon,
  highlightColor,
  state,
  text,
  onClick,
  notification,
  notificationText,
  notificationAriaLabel,
  ...props
}: Readonly<WorklineIconProps>): React.JSX.Element {
  const cName = `ehs-worklineItem ehs-worklineIcon ${state} highlight-${highlightColor}`;
  const hasTextClass = (): string => {
    if (notificationText) {
      return 'with-text';
    }
    return '';
  };

  return (
    <button
      {...props}
      type="button"
      className={cName}
      onClick={(): void => {
        onClick?.();
      }}
    >
      <div className="container">
        {notification != 'none' && (
          <div
            className={[notification, ' notification', hasTextClass()].join(
              ' '
            )}
            role="note"
          >
            {notificationText}
          </div>
        )}
        <ButtonIcon iconName={icon} />
      </div>
      <div className="text">{text}</div>
      {notification != 'none' && notificationAriaLabel && (
        //only visible for screenreaders
        <span className="sr-only">{notificationAriaLabel}</span>
      )}
    </button>
  );
}
